// Push token from the native app (security sprint PR 11). Inside the mobile app's WebView, the
// signed-in user registers the device's Expo push token on their own account through
// gnjAmplifyRegisterPushToken, instead of the native app setting it as a guest.
//
// Message contract (mobile 4.2.2 and later), native -> web. APP_ORIGIN is the web app's own
// origin for that build, exactly one of: https://app.goodnightjournal.com (production),
// https://qainla.goodnightjournal.com (qa), https://devinnyc.goodnightjournal.com (develop). `token` is token.data from
// Notifications.getExpoPushTokenAsync({ projectId }) (experienceId is deprecated).
//   if (new URL(nativeEvent.url).origin === APP_ORIGIN)   // checked at send time
//     this.webView.ref.injectJavaScript(`(function () {
//       if (location.origin !== ${JSON.stringify(APP_ORIGIN)}) return;
//       var data = ${JSON.stringify(JSON.stringify({ type: 'gnj:expoPushToken', token }))};
//       window.dispatchEvent(new MessageEvent('message', { data: data }));
//     })(); true;`)
//
// Origin gate: native sends ONLY while the WebView's top-level origin is exactly APP_ORIGIN,
// checking nativeEvent.url in BOTH onLoad/onLoadEnd and onMessage before sending; the injected
// script checks location.origin again (closes the race with a navigation). The WebView has no
// originWhitelist / onShouldStartLoadWithRequest, so outside pages load inside it (the app links
// in-frame to marketing pages; other users' journals and Stripe receipts can link anywhere), and
// any page can post { pushNotificationUsername }: answered without the gate, any page could get
// the token and register it to its own account. 4.2.2 should also open outside links in the
// system browser. (4.2.1 acting on { pushNotificationUsername } from any page is a pre-existing
// issue that step 3 closes by removing the guest path.)
//
// When: once per app launch (the first APP_ORIGIN load, if notification permission is already
// granted; never prompt there) and when the token changes, plus after each
// { pushNotificationUsername } message from APP_ORIGIN (explicit sign-in). That message is only
// a trigger: its username is not used, it prompts for permission as today, and the token message
// replaces the guest gnjAmplifyExpoNotificationToken call. Not on every load: tab-bar taps and pull-to-refresh are
// full page loads. The web app keeps the latest token in memory (per page load) and registers it
// whenever a user is signed in (explicit sign-in, restored session, or a token that arrived
// before sign-in), once per (user, token). The listener hears `window` and `document`.
//
// Anything else is ignored: the page must be inside the WebView (window.ReactNativeWebView), the
// event must be a dispatched MessageEvent with no source and no origin (a postMessage from any
// window, this one or an iframe, is dropped), the data must be { type: 'gnj:expoPushToken',
// token } (its JSON string, or an object), and `token` must pass the server's format check.
// Nothing else in the message is used. The token is never logged or shown.
//
// Sign-out: in the WebView, the web app takes the device's token off the account with
// gnjAmplifyUnregisterPushToken before the session is cleared (src/actions/user.js,
// userSignOut), so the device stops getting the previous user's pushes. The server removes it
// only if it is the account's current token. Because native sends the token once per launch and
// tab-bar taps are full page loads, the latest token is also kept in sessionStorage (this
// WebView's session, same origin only) for knownNativePushToken; registration still uses only
// tokens that arrived in this page load.

// The same check as amplify/function/gnjAmplifyExpoNotificationToken (what getExpoPushTokenAsync
// returns).
const EXPO_TOKEN = /^Expo(nent)?PushToken\[[^\]\s]{1,200}\]$/
export const MESSAGE_TYPE = 'gnj:expoPushToken'
const MAX_MESSAGE_LENGTH = 1000

export function isExpoPushToken(token) {
  return typeof token === 'string' && EXPO_TOKEN.test(token)
}

function messageData(data) {
  if (typeof data !== 'string') return data
  if (data.length > MAX_MESSAGE_LENGTH) return null
  try {
    return JSON.parse(data)
  } catch (err) {
    return null
  }
}

// The token carried by a message event that matches the contract, else null.
export function pushTokenFromMessage(event, win = window) {
  if (!win.ReactNativeWebView || !event) return null
  // Dispatched by the native app's injected script; never a postMessage from a window.
  if (event.source !== null || event.origin !== '') return null
  const data = messageData(event.data)
  if (!data || typeof data !== 'object' || data.type !== MESSAGE_TYPE) return null
  return isExpoPushToken(data.token) ? data.token : null
}

// Listens from page load (before React mounts, so a message sent at load isn't missed) and keeps
// the latest token for subscribers.
let latestToken = null
const subscribers = new Set()
let removeListener = null
const STORAGE_KEY = 'gnj:expoPushToken'

function storage(win) {
  try {
    return win.sessionStorage
  } catch (err) {
    return null
  }
}

function storeToken(win, token) {
  try {
    const s = storage(win)
    if (token) s?.setItem(STORAGE_KEY, token)
    else s?.removeItem(STORAGE_KEY)
  } catch (err) {
    // storage full or blocked: only the sign-out unregister misses it
  }
}

// The device's token from this page load, else from an earlier load in this WebView session
// (null outside the WebView). Used at sign-out.
export function knownNativePushToken(win = window) {
  if (!win.ReactNativeWebView) return null
  if (latestToken) return latestToken
  let stored = null
  try {
    stored = storage(win)?.getItem(STORAGE_KEY) ?? null
  } catch (err) {
    stored = null
  }
  return isExpoPushToken(stored) ? stored : null
}

export function startNativePushTokenListener(win = window) {
  if (removeListener) return removeListener
  const seen = new WeakSet()
  const onMessage = (event) => {
    // A bubbling event dispatched on document also reaches window.
    if (seen.has(event)) return
    seen.add(event)
    const token = pushTokenFromMessage(event, win)
    if (!token) return
    latestToken = token
    storeToken(win, token)
    subscribers.forEach((fn) => fn(token))
  }
  win.addEventListener('message', onMessage)
  win.document.addEventListener('message', onMessage)
  removeListener = () => {
    win.removeEventListener('message', onMessage)
    win.document.removeEventListener('message', onMessage)
    removeListener = null
    latestToken = null
    storeToken(win, null)
  }
  return removeListener
}

// Calls fn with the latest token now (if any) and with each new one. Returns an unsubscribe.
export function subscribeNativePushToken(fn) {
  subscribers.add(fn)
  if (latestToken) fn(latestToken)
  return () => subscribers.delete(fn)
}

// Calls register(token) once per (signed-in username, token) while that user stays signed in:
// again for a new token, another user, or the same user after a sign-out (which unregistered
// it), never without a user. The native app sends the token only once per launch, at sign-in
// and on token change (4.2.2), not on every WebView load, so a failed call is retried here on
// a timer (RETRY_DELAYS_MS) while the user and token are unchanged, and again if the native
// app does send the same token. It is logged without the token; re-renders don't retry.
export const RETRY_DELAYS_MS = [5000, 30000, 120000]
//
// The server ignores a register ({ registered: false }) when another account's newer session
// holds the token and the caller's access token was issued before that session began
// (amplify/function/gnjAmplifyExpoNotificationToken). The device's current user is then on an
// older access token than whoever took the token, so the registrar forces a token refresh
// (refreshSession) and registers once more: a fresh token wins. Once per attempt (so at most
// 1 + RETRY_DELAYS_MS.length times for one (user, token), if the retried call keeps failing), and
// not after a sign-out pause, dispose, or a change of user or token while it refreshed.

// True when a gnjAmplifyRegisterPushToken result says the call was ignored. The field is
// AWSJSON, so it arrives as a JSON string (or an object, from a test or another client).
export function registrationIgnored(result) {
  let value = result?.data?.gnjAmplifyRegisterPushToken ?? result
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch (err) {
      return false
    }
  }
  return value?.registered === false
}

// Every live registrar, so sign-out (src/actions/user.js) can pause them before it unregisters.
const registrars = new Set()

// Sign-out: stops every registrar (a pending retry is cancelled and no new call starts) and
// resolves, once any registration already in flight has settled, with one boolean per
// registrar: whether such a call registered the token. Only setUsername(null) (the app reporting
// no signed-in user) undoes the pause.
export function pausePushTokenRegistration() {
  return Promise.all([...registrars].map((r) => r.pause()))
}

export function createPushTokenRegistrar(
  register,
  schedule = setTimeout,
  cancel = clearTimeout,
  { refreshSession } = {}
) {
  let username = null
  let token = null
  let lastKey = null
  let failed = false
  let timer = null
  let attempt = 0
  let paused = false
  let disposed = false
  let inFlight = null

  const stopRetry = () => {
    if (timer !== null) cancel(timer)
    timer = null
  }

  const call = async (key) => {
    // A failed attempt (the call threw, or the session refresh before its retry failed): retried
    // on the next RETRY_DELAYS_MS timer while the user and token are unchanged.
    const retryLater = () => {
      if (lastKey !== key || paused || disposed) return false
      failed = true
      if (attempt < RETRY_DELAYS_MS.length) {
        timer = schedule(() => {
          timer = null
          if (lastKey !== key || paused || disposed) return
          failed = false
          return call(key)
        }, RETRY_DELAYS_MS[attempt++])
      }
      return false
    }
    // Resolves true when the token was registered.
    const attemptCall = (async () => {
      try {
        const sent = token
        const result = await register(sent)
        if (!registrationIgnored(result)) return true
        // Ignored: refresh the session and try once more, if nothing changed meanwhile.
        const current = () => lastKey === key && !paused && !disposed
        if (!refreshSession || !current()) return false
        try {
          await refreshSession()
        } catch (err) {
          console.log('Push token session refresh failed:', err?.name || 'unknown')
          return retryLater()
        }
        if (!current()) return false
        const retried = await register(sent)
        if (registrationIgnored(retried)) {
          console.log('Push token registration ignored: another session holds the token')
          return false
        }
        return true
      } catch (err) {
        const reason = err?.errors?.[0]?.errorType || err?.name || 'unknown'
        console.log('Push token registration failed:', reason)
        return retryLater()
      }
    })()
    inFlight = attemptCall
    try {
      await attemptCall
    } finally {
      if (inFlight === attemptCall) inFlight = null
    }
  }

  const maybeRegister = async () => {
    if (paused || disposed || !username || !token) return
    const key = `${username}\n${token}`
    if (key === lastKey) return
    stopRetry()
    attempt = 0
    lastKey = key
    failed = false
    await call(key)
  }

  const registrar = {
    setUsername(value) {
      username = value || null
      if (!username) {
        lastKey = null
        stopRetry()
        // Signed out: the next sign-in registers again. This is the only place a pause ends.
        paused = false
      }
      return maybeRegister()
    },
    setToken(value) {
      if (!isExpoPushToken(value)) return Promise.resolve()
      if (failed && value === token) lastKey = null
      token = value
      return maybeRegister()
    },
    // See pausePushTokenRegistration. Undone only by setUsername(null). Resolves true when a
    // call in flight registered the token.
    async pause() {
      paused = true
      stopRetry()
      lastKey = null
      failed = false
      return inFlight ? inFlight : false
    },
    // For good (the app unmounted): no new call or retry, and sign-out no longer waits on it.
    dispose() {
      disposed = true
      stopRetry()
      registrars.delete(registrar)
    },
  }
  registrars.add(registrar)
  return registrar
}
